import { forwardRef, useId } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { ICON_SIZE, ICON_STROKE, type LucideIcon } from '@/components/icons'

/** The only text input in the product. */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  /** Shown below the field until an error replaces it. */
  hint?: ReactNode
  /** Any truthy value puts the field in its error state and is announced. */
  error?: string | null
  icon?: LucideIcon
  /** Rendered inside the field on the right — a copy button, a generate button. */
  trailing?: ReactNode
  containerClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, icon: Icon, trailing, className, containerClassName, id, required, ...props },
  ref,
) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {label && (
        <label htmlFor={inputId} className="text-xs font-medium text-muted">
          {label}
          {required && (
            <span className="ml-0.5 text-danger" aria-hidden>
              *
            </span>
          )}
        </label>
      )}

      <div className="relative flex items-center">
        {Icon && (
          <Icon
            size={ICON_SIZE.sm}
            strokeWidth={ICON_STROKE}
            aria-hidden
            className="pointer-events-none absolute left-3 text-subtle"
          />
        )}

        <input
          ref={ref}
          id={inputId}
          required={required}
          // Both are needed: aria-invalid marks the field, aria-describedby
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-9 w-full rounded-md border bg-surface px-3 text-base text-text',
            'placeholder:text-subtle',
            'transition-colors duration-150',
            'focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-primary',
            'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-subtle',
            Icon && 'pl-9',
            trailing && 'pr-10',
            error ? 'border-danger-border' : 'border-line hover:border-line-strong',
            className,
          )}
          {...props}
        />

        {trailing && <div className="absolute right-1 flex items-center">{trailing}</div>}
      </div>

      {error ? (
        // role="alert" so a validation failure is announced when it appears,
        <p id={`${inputId}-error`} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-xs text-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  )
})
